<template>
  <div class="view-invoices">
    <div class="d-flex justify-content-between align-items-center mb-5">
      <h4 class="text-semibold">Detalhes de eNotas</h4>
      <snow-button
        variant="outline"
        size="md"
        right-icon="ph ph-arrow-square-out text-dark-40"
        :text="isSmallScreen ? '' : 'Ir para a integração'"
        @click="$router.push({ name: 'tools-enotas' })"
      />
    </div>

    <div v-if="cardsLoading" class="text-center my-9">
      <b-spinner variant="primary" />
    </div>

    <div class="d-flex flex-lg-row flex-column gap-4 mb-7" v-else>
      <info-card
        prefix=""
        class="flex-fill"
        icon="ph ph-cardholder"
        title="Total de notas fiscais"
        :value="invoicesTotal.total_invoices"
      />
      <info-card
        prefix=""
        class="flex-fill"
        icon="ph ph-file-arrow-up"
        title="Enviadas"
        :value="invoicesSent.total_invoices"
      />
      <info-card
        prefix=""
        class="flex-fill"
        icon="ph ph-clock"
        title="Pendentes"
        :value="invoicesPending.total_invoices"
      />
    </div>
    <snow-data-table-header
      @searching="searching"
      @open-filters="filtersModal = true"
      :datePicker="false"
      :add="false"
    >
      <snow-button
        size="sm"
        text="Exportar"
        variant="borderless"
        icon="ph ph-download-simple"
        @click="exportCSV"
      />
    </snow-data-table-header>
    <div v-if="loading" class="text-center my-9">
      <b-spinner variant="primary" />
    </div>
    <div
      v-else-if="!invoices.length"
      class="container centralize-row text-center pt-6"
    >
      <div class="row justify-content-center">
        <div class="col-lg-5">
          <img
            class="d-block mb-6 mx-auto"
            src="@/assets/images/no-content.svg"
          />
          <template v-if="filters.search">
            <h5>Nenhum resultado encontrado</h5>
          </template>

          <template v-else>
            <h5>Você ainda não enviou notas!</h5>
          </template>
        </div>
      </div>
    </div>
    <div v-else>
      <!-- Desktop -->
      <data-table
        new-design
        class="d-none d-lg-block"
        columnsFr="0.5fr 1fr 0.7fr 0.5fr 0.5fr 0.5fr 0.3fr 0.1fr"
        :data="invoices"
        :columns="columns"
      >
        <template #default="{ item }">
          <span>
            {{ item.paid_at | formatDate }}
          </span>
          <div class="crop">
            <div class="fw-5">
              {{ item.shopper_name }}
            </div>
            <div class="text-secondary">
              {{ item.shopper_email }}
            </div>
          </div>
          <div class="crop">
            <div class="fw-5">{{ item.product_name }}</div>
          </div>
          <span>R$ {{ formatMoney(item.amount) }}</span>
          <span>
            {{ item.payment_method }}
          </span>
          <span> {{ item.send_at | formatDate }}</span>
          <span>
            <span :class="formatStatusClass(item.status)">
              {{ item.status | formatStatus }}
            </span>
          </span>
          <div class="d-flex align-items-center justify-content-end">
            <actions-dropdown
              :left="false"
              :right="true"
              :actions="actions"
              @open-details="openDetails(item)"
            />
          </div>
        </template>
      </data-table>
      <!-- Desktop end -->
      <!-- Mobile -->
      <data-table
        new-design
        class="d-lg-none"
        :data="invoices"
        :firstColumnFr="2"
        :lastColumnFr="0.3"
        :columns="['', '']"
      >
        <template #default="{item}">
          <div class="d-flex flex-column responsive-table py-5">
            <span>
              <strong>Comprador: </strong>
              {{ item.shopper_name }}
            </span>
            <span>
              <strong>Produto: </strong>
              {{ item.product_name }}
            </span>
            <span>
              <strong>Valor: </strong>R$
              {{ formatMoney(item.amount) }}
            </span>
            <span>
              <strong>Status: </strong>
              <span :class="formatStatusClass(item.status)">
                {{ item.status | formatStatus }}
              </span>
            </span>
          </div>
          <div class="d-flex align-items-top justify-content-end">
            <actions-dropdown
              :left="false"
              :right="true"
              :actions="actions"
              @open-details="openDetails(item)"
            />
          </div>
        </template>
      </data-table>
    </div>
    <div class="d-flex justify-content-end mt-4">
      <snow-paginator
        v-model="page"
        :pagination="pagination"
        @change-page="dispatch"
      />
    </div>
    <invoice-details
      v-model="modal"
      :invoice="focusedInvoice"
      @refresh="refresh"
    />

    <responsive-modal
      v-model="filtersModal"
      title="Filtrar"
      description="Selecione os filtros e aplique"
    >
      <invoices-filters
        v-model="filters"
        @input="filtersModal = false"
        @close="filtersModal = false"
        @onFiltersApply="onFiltersApply"
      />
    </responsive-modal>
  </div>
</template>
<script>
import { SALE_PAY_METHOD_NAME_MAP } from "@/constants/sales.js";
import { clearObject, datesString, formatMoney } from "@/functions/helpers";

import InvoiceDetails from "@/components/sale/InvoiceDetails";
import InvoicesFilters from "@/components/sale/InvoicesFilters";

import moment from "moment";
import { mapState } from "vuex";

export default {
  components: {
    InvoicesFilters,
    InvoiceDetails
  },
  data() {
    return {
      invoicesTotal: {
        total_invoices: 0,
        amount_invoices: ""
      },
      invoicesSent: {
        total_invoices: 0,
        amount_invoices: ""
      },
      invoicesPending: {
        total_invoices: 0,
        amount_invoices: ""
      },
      loading: false,
      cardsLoading: false,
      exportLoading: false,
      modal: false,
      filtersModal: false,
      focusedInvoice: null,
      page: 1,
      actions: [
        {
          text: "Detalhes",
          icon: "icon-exclamation-outline",
          confirmation: {
            enable: false,
            text: ""
          },
          event: "open-details"
        }
      ],
      columns: [
        "Competência",
        "Comprador",
        "Produto",
        "Valor",
        "Pagamento",
        "Emitida",
        "Status",
        ""
      ],
      filters: {
        offset: 0,
        status: "",
        search: "",
        product_id: "",
        send_at_after: "",
        send_at_before: "",
        paid_at_after: "",
        paid_at_before: ""
      }
    };
  },
  filters: {
    formatStatus(val) {
      let options = {
        sent: "Enviado",
        pending: "Pendente",
        failed: "Falha",
        canceled: "Cancelado"
      };
      return options[val];
    },
    formatDate(val) {
      if (val) val = val.replace("Z", "");
      return val ? moment(val).format("DD/MM/YYYY") : "-";
    }
  },
  computed: {
    ...mapState("product", ["products"]),
    ...mapState("invoiceNfe", ["invoices", "pagination"]),
    isSmallScreen() {
      return window.innerWidth < 992;
    }
  },
  watch: {
    filters: {
      handler() {
        this.dispatch();
      },
      deep: true
    }
  },
  mounted() {
    this.dispatch();
    this.getTotals();
  },
  methods: {
    formatStatusClass(val) {
      let options = {
        sent: "active-badge",
        pending: "draft-badge",
        failed: "inactive-badge",
        canceled: "incomplete-badge"
      };
      return options[val];
    },
    getPayMethod(payment_method) {
      if (payment_method) {
        return SALE_PAY_METHOD_NAME_MAP[payment_method.toLowerCase()];
      }
      return "-";
    },
    async exportCSV() {
      try {
        this.exportLoading = true;

        let params = { ...this.filters, export: true };

        const CSVContent = await this.$store
          .dispatch("invoiceNfe/getInvoicesCSV", params)
          .then();
        var a = document.createElement("a");

        a.href = `data:text/csv;charset=utf-8,${CSVContent.replaceAll(
          "#",
          "%23"
        )}`;
        a.setAttribute(
          "download",
          `invoices_${moment().format("YYYYMMDDhhmmss")}.csv`
        );
        a.click();
      } catch (e) {
      } finally {
        this.exportLoading = false;
      }
    },
    searching(search) {
      this.filters.search = search;
      this.dispatch();
    },
    openDetails(item) {
      this.focusedInvoice = item;
      this.modal = true;
    },
    refresh() {
      this.dispatch();
    },
    dispatch() {
      this.loading = true;
      this.$store
        .dispatch("invoiceNfe/getInvoices", clearObject(this.filters))
        .finally(() => {
          this.loading = false;
        });
    },
    getTotals() {
      this.cardsLoading = true;
      Promise.all([
        this.$store
          .dispatch("invoiceNfe/getInvoicesTotal")
          .then(res => (this.invoicesTotal = res)),
        this.$store
          .dispatch("invoiceNfe/getInvoicesTotal", { status: "sent" })
          .then(res => (this.invoicesSent = res)),
        this.$store
          .dispatch("invoiceNfe/getInvoicesTotal", { status: "pending" })
          .then(res => (this.invoicesPending = res))
      ]).finally(() => (this.cardsLoading = false));
    },
    onFiltersApply() {
      this.page = 1;
      this.filters.offset = 0;
      this.dispatch();
    },
    formatMoney,
    datesString
  }
};
</script>
<style lang="scss" scoped>
.integration-link {
  color: var(--dark);
  font-weight: 600;
  font-size: 14px;
  &:hover {
    text-decoration: underline;
  }
}
</style>
